import { Card } from "@/types/v2";
import { firestoreToDateTime } from "@/lib/utils";
import {
  PendingToast,
  planToastStack,
  selectPendingToasts,
} from "@/lib/toastNotificationSelectors";

/**
 * A pending toast still on screen, with its expiry snapshotted at the
 * moment it was first added to the accumulator (not re-read from the card
 * on every cycle — the card's `toastNotifications` array can be overwritten
 * or cleared by a later automation firing, and that must not retroactively
 * change the expiry of a toast already being displayed).
 */
export type ActiveToast = PendingToast & { expiresAt: Date };

/**
 * Whether this manager's toast is *still* the one in the shared slot — i.e.
 * whether it actually reached the screen and stayed there, rather than being
 * evicted.
 *
 * The slot is a lossy channel in the inbound direction: `ADD_TOAST` slices to
 * TOAST_LIMIT=1 (useToast.tsx), so this manager's toast can vanish
 * milliseconds after being shown. Two things have since narrowed that, but
 * neither closes it. Eviction is no longer silent — dispatch() calls the
 * sliced toast's `onOpenChange(false, "evicted")`, except for a toast already
 * mid-close from its own dismissal (the `open !== false` filter). And
 * MyOverdueTaskToastManager no longer shows unconditionally — it checks
 * occupancy via `canClaimToastSlot` first. But a higher-priority manager is
 * still entitled to preempt us, so "shown" still does not imply "stayed on
 * screen". Arbitration guards only the outbound direction (don't evict
 * someone you shouldn't); this is the inbound counterpart, and it is what
 * makes "was it delivered?" answerable before recording anything durable
 * about the firing.
 *
 * Strict equality, and an empty slot counts as NOT ours: with a toast of our
 * own outstanding, an empty slot means ours was sliced out and the evictor
 * has since gone away.
 * @param {string | null} slotOccupantId - id of the toast currently in the
 *   shared slot, or null if the slot is empty.
 * @param {string | null} ownToastId - id of the toast this manager last
 *   showed, or null if it isn't currently showing one.
 * @returns {boolean} True only when the slot still holds this manager's toast.
 */
export function ownsToastSlot(
  slotOccupantId: string | null,
  ownToastId: string | null
): boolean {
  return ownToastId !== null && slotOccupantId === ownToastId;
}

export type AutomationToastPlan =
  | { type: "none" }
  | { type: "dismiss" }
  /**
   * There's something new/changed to show, but the shared toast slot is
   * currently held by another manager's toast (a customer update mid-edit,
   * an overdue-task toast). Carries no `newKeys` or `active` on purpose —
   * that makes it structurally impossible for the caller to mark anything
   * seen or advance the accumulator for a deferred cycle. Nothing is
   * consumed, so the same (or a grown) set of pending toasts is offered
   * again on the next cycle once the slot frees.
   */
  | { type: "defer" }
  | {
      type: "show";
      /** The full accumulated set now on screen — store this in the ref. */
      active: ActiveToast[];
      /** Freshly pending keys this cycle — mark these seen. */
      newKeys: string[];
      visible: PendingToast[];
      overflow: number;
    };

/**
 * Decides what AutomationToastManager should do this cycle: nothing, tear
 * down the toast, or (re)show it with an updated set of rows.
 *
 * `selectPendingToasts` only ever returns a given key once — once a toast
 * is marked seen it is permanently excluded from future results, even
 * while it is still legitimately on screen and unexpired. Concurrent
 * firings (card A's toast, then card B's toast half a second later, each
 * its own store snapshot) would otherwise replace one another instead of
 * stacking, which is the "queue" behavior the design explicitly rejected in
 * favor of a stacked toast. `active` is the accumulator that fixes this: it
 * carries forward whatever is still legitimately on screen, unioned with
 * whatever is freshly pending this cycle, pruned of anything whose expiry
 * has since passed (so a long-lived board doesn't accumulate forever).
 *
 * Pure and side-effect-free: the caller (AutomationToastManager) owns
 * mutating `seenKeys` (via `newKeys`) and the `active` ref, and owns the
 * auto-dismiss timer.
 * @param {Card[]} cards - All cards in the store.
 * @param {Date} now - Current wall-clock time.
 * @param {ReadonlySet<string>} seenKeys - Keys this client has already shown.
 * @param {ActiveToast[]} active - What's currently accumulated on screen.
 * @param {number} visibleLimit - How many rows the stack renders.
 * @param {boolean} slotFreeOrOurs - Whether the shared toast slot (see
 *   useToast.tsx's TOAST_LIMIT=1) is currently empty or occupied by this
 *   manager's own toast, as opposed to a foreign toast (customer update,
 *   overdue task) it must not evict. Defaults to `true` so existing callers
 *   that don't reason about the slot keep prior behavior; the real caller
 *   (AutomationToastManager) always passes the computed value explicitly.
 *   When `false` and there's otherwise something new to show, the cycle
 *   defers instead of showing — see the `defer` plan type.
 * @returns {AutomationToastPlan} What the manager should do this cycle.
 */
export function planAutomationToastCycle(
  cards: Card[],
  now: Date,
  seenKeys: Set<string>,
  active: ActiveToast[],
  visibleLimit: number,
  slotFreeOrOurs = true
): AutomationToastPlan {
  const pending = selectPendingToasts(cards, seenKeys, now);

  const newActive: ActiveToast[] = pending.reduce<ActiveToast[]>((acc, p) => {
    const card = cards.find((c) => c.id === p.cardId);
    const expiresAt = card
      ? firestoreToDateTime(card.toastNotifications?.[p.index]?.expiresAt)
      : null;
    // Unreachable in practice: `p` came from selectPendingToasts iterating
    // this same `cards` array at this same `index`, which already validated
    // a parseable expiresAt on this exact payload. Guarded defensively
    // rather than asserted, so a future refactor that breaks the invariant
    // fails safe (skips the toast) instead of throwing.
    if (expiresAt) acc.push({ ...p, expiresAt });
    return acc;
  }, []);

  // Append order is load-bearing: `active` (already on screen, oldest-first)
  // followed by `newActive` (this cycle's arrivals, also oldest-first from
  // selectPendingToasts) keeps `combined` oldest-first across cycles, not
  // just within one. `planToastStack` relies on that ordering to take the
  // tail as "newest" when it caps visible rows.
  const combined = [...active, ...newActive].filter(
    (t) => t.expiresAt.getTime() > now.getTime()
  );

  const changed = newActive.length > 0 || combined.length !== active.length;

  if (combined.length === 0) {
    return active.length > 0 ? { type: "dismiss" } : { type: "none" };
  }

  if (!changed) return { type: "none" };

  // Gate BEFORE any keys are consumed: this is the only place a "show"
  // would otherwise be returned, so refusing here — before newKeys/active
  // are ever constructed for the caller — is what keeps a deferred toast's
  // keys unmarked and its payloads genuinely pending for the next cycle.
  if (!slotFreeOrOurs) return { type: "defer" };

  const { visible, overflow } = planToastStack(combined, visibleLimit);

  return {
    type: "show",
    active: combined,
    newKeys: pending.map((p) => p.key),
    visible,
    overflow,
  };
}

/**
 * Removes seen-toast bookkeeping whose eligibility window has passed.
 *
 * `seenKeys`/`seenExpiry` are expected to live at module scope (see
 * AutomationToastManager) so they survive the manager's own remounts —
 * which means nothing else ever clears them, so without pruning they'd grow
 * for as long as the tab stays open. Safe to call on every cycle: a key
 * whose card notification has already expired is independently filtered out
 * by `selectPendingToasts` regardless of whether it's still present here, so
 * pruning is memory hygiene only, never a correctness dependency.
 * @param {Set<string>} seenKeys - The seen-key set `selectPendingToasts` checks.
 * @param {Map<string, number>} seenExpiry - Side table of key -> expiresAt (ms),
 *   recorded when each key was first marked seen.
 * @param {Date} now - Current wall-clock time.
 * @returns {boolean} True when at least one entry was removed — the caller
 *   (AutomationToastManager) uses this to persist only on an actual change,
 *   since its effect re-runs on every `cards` update and every 30s tick.
 */
export function pruneExpiredSeenToastKeys(
  seenKeys: Set<string>,
  seenExpiry: Map<string, number>,
  now: Date
): boolean {
  const nowMs = now.getTime();
  let removed = false;
  for (const [key, expiresAtMs] of seenExpiry) {
    if (expiresAtMs <= nowMs) {
      seenExpiry.delete(key);
      seenKeys.delete(key);
      removed = true;
    }
  }
  return removed;
}
